import { useEffect, useRef } from "react";
import backgroundMusic from "@/assets/George Michael - Careless Whisper (Official Video).mp3";

// Интерфейс пропсов для игрового аудио
interface GameAudioProps {
  soundEffect?: string;  // Путь к звуковому эффекту
  volume?: number;       // Громкость (по умолчанию 0.3)
}

// Компонент для воспроизведения звуковых эффектов
export const GameAudio = ({ soundEffect, volume = 0.3 }: GameAudioProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);  // Ссылка на аудио элемент

  useEffect(() => {
    if (soundEffect && audioRef.current) {
      audioRef.current.currentTime = 0;     // Сбрасываем время воспроизведения
      audioRef.current.volume = volume;     // Устанавливаем громкость
      audioRef.current.play().catch(() => {
        // Воспроизведение не удалось (пользователь еще не взаимодействовал)
      });
    }
  }, [soundEffect, volume]);

  return (
    <audio ref={audioRef} preload="auto">
      <source src="/placeholder-sound.mp3" type="audio/mpeg" />
    </audio>
  );
};

// Интерфейс пропсов для фоновой музыки
interface BackgroundMusicProps {
  isPlaying: boolean;  // Флаг воспроизведения
  volume?: number;     // Громкость (по умолчанию 0.2)
}

// Компонент для воспроизведения фоновой музыки
export const BackgroundMusic = ({ isPlaying, volume = 0.2 }: BackgroundMusicProps) => {
  const audioRef = useRef<HTMLAudioElement>(null);  // Ссылка на аудио элемент

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;  // Устанавливаем громкость
      audioRef.current.loop = true;     // Включаем зацикливание
      
      if (isPlaying) {
        audioRef.current.play().catch(() => {
          // Воспроизведение не удалось (пользователь еще не взаимодействовал)
        });
      } else {
        audioRef.current.pause();  // Ставим на паузу
      }
    }
  }, [isPlaying, volume]);

  return (
    <audio ref={audioRef} preload="auto">
      <source src={backgroundMusic} type="audio/mpeg" />
    </audio>
  );
};

// Создание аудио контекста для генерации звуков
export const playCollectSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();  // Создаем осциллятор
    const gainNode = audioContext.createGain();          // Создаем узел громкости
    
    oscillator.connect(gainNode);           // Подключаем осциллятор к узлу громкости
    gainNode.connect(audioContext.destination);  // Подключаем к выходу
    
    // Настраиваем частоту звука (восходящий тон)
    oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1200, audioContext.currentTime + 0.1);
    
    // Настраиваем громкость (затухающий звук)
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
    
    oscillator.start(audioContext.currentTime);      // Начинаем воспроизведение
    oscillator.stop(audioContext.currentTime + 0.1); // Останавливаем через 0.1 сек
  } catch (e) {
    // Аудио не поддерживается
  }
};

// Функция воспроизведения звука усиления
export const playPowerUpSound = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();  // Создаем осциллятор
    const gainNode = audioContext.createGain();          // Создаем узел громкости
    
    oscillator.connect(gainNode);           // Подключаем осциллятор к узлу громкости
    gainNode.connect(audioContext.destination);  // Подключаем к выходу
    
    // Настраиваем частоту звука (восходящий тон для усиления)
    oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(800, audioContext.currentTime + 0.2);
    
    // Настраиваем громкость (затухающий звук)
    gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
    
    oscillator.start(audioContext.currentTime);      // Начинаем воспроизведение
    oscillator.stop(audioContext.currentTime + 0.2); // Останавливаем через 0.2 сек
  } catch (e) {
    // Аудио не поддерживается
  }
};